<template>
    <div v-if="open" class="calc-modal-backdrop" @click.self="$emit('close')">
        <div class="calc-modal" role="dialog" aria-modal="true">
            <h2 class="calc-modal__title">{{ $t('§calc.step3.pdf.modal_title') }}</h2>

            <div ref="recaptchaContainer" class="calc-modal__captcha"></div>

            <div class="calc-modal__notice">
                <span class="calc-modal__notice-icon">
                    <CalcIcon name="shield-check" />
                </span>
                <span>{{ $t('§calc.step3.pdf.modal_data_notice') }}</span>
            </div>

            <CalcButton
                v-if="captchaPassed"
                variant="primary"
                :label="'§calc.step3.pdf.modal_confirm'"
                :disabled="isSubmitting"
                @click="handleConfirm"
            />

            <CalcButton
                variant="bare"
                :label="'§ui_button.cancel'"
                @click="$emit('close')"
            />
        </div>
    </div>
</template>

<script>
import CalcButton from './CalcButton.vue'
import CalcIcon from './CalcIcon.vue'
import { whenRecaptchaReady, readSiteKey } from '../helpers/recaptcha.js'

export default {
    name: 'CalcPdfDownloadModal',

    components: { CalcButton, CalcIcon },

    props: {
        open: { type: Boolean, default: false },
    },

    emits: ['confirm', 'close'],

    data() {
        return {
            captchaPassed: false,
            captchaToken: '',
            recaptchaWidgetId: null,
            isSubmitting: false,
        }
    },

    watch: {
        open: {
            immediate: true,
            async handler(isOpen) {
                if (isOpen) {
                    await this.$nextTick()
                    this.armRecaptcha()
                } else {
                    this.resetCaptcha()
                }
            },
        },
    },

    beforeUnmount() {
        this.resetCaptcha()
    },

    methods: {
        async armRecaptcha() {
            try {
                await whenRecaptchaReady()
            } catch (e) {
                void e
                return
            }
            if (!this.$refs.recaptchaContainer) return
            if (this.recaptchaWidgetId !== null) {
                if (typeof window.grecaptcha?.reset === 'function') {
                    window.grecaptcha.reset(this.recaptchaWidgetId)
                }
                this.captchaPassed = false
                this.captchaToken = ''
                return
            }
            this.recaptchaWidgetId = window.grecaptcha.render(this.$refs.recaptchaContainer, {
                sitekey: readSiteKey(),
                callback: this.onCaptchaSuccess,
                'expired-callback': this.onCaptchaExpired,
                'error-callback': this.onCaptchaError,
            })
        },

        onCaptchaSuccess(token) {
            this.captchaToken = token
            this.captchaPassed = true
        },

        onCaptchaExpired() {
            this.captchaPassed = false
            this.captchaToken = ''
        },

        onCaptchaError() {
            this.captchaPassed = false
            this.captchaToken = ''
        },

        resetCaptcha() {
            this.captchaPassed = false
            this.captchaToken = ''
            this.isSubmitting = false
            if (
                this.recaptchaWidgetId !== null &&
                typeof window.grecaptcha?.reset === 'function'
            ) {
                window.grecaptcha.reset(this.recaptchaWidgetId)
            }
            this.recaptchaWidgetId = null
        },

        handleConfirm() {
            if (!this.captchaPassed || this.isSubmitting) return
            this.isSubmitting = true
            this.$emit('confirm', { captchaToken: this.captchaToken })
        },
    },
}
</script>

<style scoped>
.calc-modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 999;
}

.calc-modal {
    background: white;
    border-radius: 16px;
    padding: 32px 40px;
    width: min(420px, 90vw);
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
    box-shadow: 0 24px 64px rgba(0, 0, 0, 0.2);
}

.calc-modal__title {
    margin: 0;
    font-size: 20px;
    line-height: 28px;
    color: var(--calc-ink);
}

.calc-modal__captcha {
    min-height: 78px;
    display: flex;
    justify-content: center;
}

.calc-modal__notice {
    background: var(--calc-green-mint);
    border-radius: 12px;
    padding: 16px 18px;
    display: flex;
    gap: 12px;
    align-items: flex-start;
    font-size: 14px;
    line-height: 1.5;
    color: var(--calc-ink);
}

.calc-modal__notice-icon {
    flex: 0 0 auto;
    color: var(--calc-green-deep);
    margin-top: 1px;
}

.calc-modal__notice-icon :deep(svg) {
    display: block;
}
</style>
